import { ReactNode, useCallback, useEffect } from "react";
import { USER_LOGGED_IN_KEY } from "@/constants";
import { clearLS } from "@/utils/auth";
import { config } from "@/utils/config";
import { useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import useMyProfile from "@/hooks/authentication/useMyProfile";
import useDashboardShare, { DASHBOARD_ID } from "@/hooks/dashboardShare";
import { AuthContext } from "./AuthContext";
import { User } from "./authentication.type";
import { userLoggedInAtom } from "./store";

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { setItem } = useDashboardShare();
  const queryString = window.location.search;
  if (queryString) {
    const params = new URLSearchParams(queryString);
    // const token = params.get("token") || "";
    const dashboardId = params.get("did") || "";
    if (dashboardId) {
      // setItem(NEW_SHARE_TOKEN, token);
      setItem(DASHBOARD_ID, dashboardId);
    }
  }
  const queryClient = useQueryClient();
  const [profile, setProfile] = useAtom<string | null>(userLoggedInAtom);
  const { data: sessionUser, isPending } = useMyProfile();

  const login = (user: User | undefined) => {
    if (!user) {
      logout();
      return;
    }
    setProfile(JSON.stringify(user));
  };

  const logout = useCallback(() => {
    queryClient.clear();
    setProfile(null);
    clearLS();
  }, [queryClient, setProfile]);

  useEffect(() => {
    function checkLocalStorage() {
      const localStorageValue = localStorage.getItem(USER_LOGGED_IN_KEY);
      if (!localStorageValue) {
        logout();
      }
    }
    window.addEventListener("focus", checkLocalStorage);
    return () => {
      window.removeEventListener("focus", checkLocalStorage);
    };
  }, [logout]);

  const parsedUser: User =
    profile !== null && typeof profile === "string" ? JSON.parse(profile) : profile || null;

  return (
    <AuthContext.Provider
      value={{
        isPending: config().ENABLED_SAML ? isPending : false,
        profile: parsedUser,
        isLoggedIn: config().ENABLED_SAML
          ? Boolean(sessionUser?.data?.user?.MIMSLoginID) && Boolean(parsedUser)
          : Boolean(parsedUser),
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
